// rehab.ts

import { useMutation, useQuery } from "@tanstack/react-query"
import { sdk } from "@/api/sdk"
import { queryClient } from "./client"
import {
  type EditRehabMutationVariables,
  type SetBudgetPropertyTypeInput,
  type SubmitRehabBudgetInput,
  type UpdateBudgetPropertyDetailsInput,
  type UpdatePrimaryDrawsContactInput,
  type UpsertBudgetLineItemInput,
} from "@/generated/graphql"

// Fetch a single rehab budget
// This accepts either a rehabBudgetId or a loanApplicationId.
// If a loanApplicationId is provided, the query uses it to automatically
// get or create a rehab budget on the backend.
export const useRehabBudget = (
  rehabBudgetId: string | undefined,
  loanApplicationId: string | undefined,
  enabled: boolean = false
) => {
  return useQuery({
    queryKey: loanApplicationId
      ? ["rehabBudget", loanApplicationId, enabled]
      : ["rehabBudget", rehabBudgetId, enabled],
    queryFn: async () => {
      try {
        // Prioritize loanApplicationId so the backend uses get_or_create logic.
        if (loanApplicationId) {
          const response = await sdk.Rehab({ loanApplicationId })

          // Expect the response to include a rehabBudget with a valid id
          if (!response?.rehabBudget?.id) {
            throw new Error("No rehabBudget id returned")
          }
          return response
        } else if (rehabBudgetId) {
          console.error("rehabBudgetId provided without loanApplicationId")
          return null
        } else {
          return null
        }
      } catch (err) {
        console.error("Error fetching rehab", err)
        throw err
      }
    },
    // Only run the query if both enabled and we have a loanApplicationId.
    enabled: enabled && !!loanApplicationId,
    refetchOnMount: enabled,
  })
}

export const useEditRehab = () => {
  return useMutation({
    mutationFn: async (editPayload: EditRehabMutationVariables) => {
      try {
        return await sdk.EditRehab(editPayload)
      } catch (err) {
        console.error("Error editing rehab", err)
        throw err
      }
    },
    onSuccess: async (data) => {
      console.log("EditRehab mutation success", data)
      const rehabId = data?.updateRehabBudget?.rehabBudget?.id
      if (rehabId) {
        await queryClient.invalidateQueries({ queryKey: ["rehabBudget"] })
        await queryClient.invalidateQueries({ queryKey: ["rehab", rehabId] })
      }
      await queryClient.invalidateQueries({ queryKey: ["rehabs"] })
    },
  })
}

// Upsert a Budget Line Item (mutation)
export const useUpsertBudgetLineItem = (rehabBudgetId: string) => {
  return useMutation({
    mutationFn: async (input: UpsertBudgetLineItemInput) => {
      try {
        return await sdk.UpdateBudgetLineItemMutation({ input })
      } catch (err) {
        console.error("Error updating budget line item", err)
        throw err
      }
    },
    onSuccess: async () => {
      // Invalidate relevant queries so the UI stays in sync.
      await queryClient.invalidateQueries({ queryKey: ["rehabBudget"] })
    },
  })
}

export const useUpdateBudgetPropertyDetails = () => {
  return useMutation({
    mutationFn: async (input: UpdateBudgetPropertyDetailsInput) => {
      try {
        return await sdk.UpdateBudgetPropertyDetailsMutation({ input })
      } catch (err) {
        console.error("Error updating property details", err)
        throw err
      }
    },
    onSuccess: async () => {
      // Invalidate rehab-related queries to refresh UI
      await queryClient.invalidateQueries({ queryKey: ["rehabBudget"] })
    },
  })
}

// Set Initial Budget Property Type
export const useSetInitialBudgetPropertyType = () => {
  return useMutation({
    mutationFn: async (input: SetBudgetPropertyTypeInput) => {
      try {
        return await sdk.SetBudgetInitialPropertyTypeMutation({ input })
      } catch (err) {
        console.error("Error setting initial budget property type", err)
        throw err
      }
    },
    onSuccess: async () => {
      // Invalidate relevant queries so the UI stays in sync.
      await queryClient.invalidateQueries({ queryKey: ["rehabBudget"] })
    },
  })
}

// Primary Draws
export const useUpdatePrimaryDrawsContact = () => {
  return useMutation({
    mutationFn: async (input: UpdatePrimaryDrawsContactInput) => {
      try {
        return await sdk.UpdatePrimaryDrawsContactMutation({ input })
      } catch (err) {
        console.error("Error updating primary draws contact", err)
        throw err
      }
    },
    onSuccess: async () => {
      // Invalidate relevant queries so the UI stays in sync.
      await queryClient.invalidateQueries({ queryKey: ["rehabBudget"] })
    },
  })
}

// Submit Rehab Budget mutation
export const useSubmitRehabBudget = () => {
  return useMutation({
    mutationFn: async (input: SubmitRehabBudgetInput) => {
      try {
        return await sdk.SubmitRehabBudgetMutation({ input })
      } catch (err) {
        console.error("Error submitting rehab budget", err)
        throw err
      }
    },
    onSuccess: async () => {
      // Invalidate relevant queries so the UI stays in sync.
      await queryClient.invalidateQueries({ queryKey: ["rehabBudget"] })
    },
  })
}

// TODO: Ask Jared if we have an ArchiveRehab mutation method
// Probably a future feature...
/* export const useArchiveRehab = () => {
  return useMutation({
    mutationFn: async (archivePayload: ArchiveLoanMutationVariables) => {
      try {
        return await sdk.ArchiveRehab(archivePayload)
      } catch (err) {
        console.error("Error archiving rehab", err)
        throw err
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["rehabs"] })
    },
  })
} */
